import { BonusPeriodLiteModel, useGetBonusPeriods } from 'api-hooks/bonus';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import PaginationComponent from 'components/elements/pagination-component';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useNavigation from 'hooks/use-navigation';
import { commonColumnTable } from 'legacy-modules/common/column';
import { Container, TableContainer, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { NavigationProps } from 'types';

export type BonusPeriodListParams = null;

export default function BonusPeriodList(
  props: NavigationProps<NavigationRoutes.BonusPeriodList>,
) {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const navigation = useNavigation();
  const formatDate = useDate();

  const permission = useGetCrudPermission('commission-masters');

  const onClickCreateBonusPeriod = React.useCallback(() => {
    if (permission.create) {
      navigation.navigate(NavigationRoutes.BonusPeriodCreate);
    }
  }, [navigation, permission.create]);

  const _columns = React.useMemo<IColumn<BonusPeriodLiteModel>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common:name'),
        sortName: 'name',
      },
      {
        accessorKey: 'status',
        header: t('common:status'),
        sortName: 'status',
        cell: ({ getValue }) => <>{t(`common:${getValue() as string}`)}</>,
      },
      {
        header: t('common:issue_at'),
        accessorKey: 'issueAt',
        minSize: 150,
        cell: ({ getValue }) => formatDate(getValue() as Date),
      },
      {
        header: t('common:started_at'),
        accessorKey: 'startAt',
        minSize: 150,
        cell: ({ getValue }) => formatDate(getValue() as Date),
      },
      {
        header: t('common:ended_at'),
        accessorKey: 'endAt',
        minSize: 150,
        cell: ({ getValue }) => formatDate(getValue() as Date),
      },
      ...commonColumnTable(t),
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetBonusPeriods,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const cols = React.useMemo<IColumn<BonusPeriodLiteModel>[]>(() => {
    const meta = data?.meta;
    return [
      {
        header: '#',
        accessorKey: 'index',
        minSize: 40,
        maxSize: 40,
        stickyLeft: true,

        cell: ({ row }) => {
          return (
            <>
              {row.index +
                1 +
                ((meta?.currentPage || 1) - 1) * (meta?.perPage || 1)}
            </>
          );
        },
      },
      ...columns,
    ];
  }, [columns, data?.meta]);

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:bonus-period')}
          showAdd={permission.create}
          onCreate={onClickCreateBonusPeriod}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.BonusPeriodCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <TableContainer>
          <TableComponent
            columns={cols}
            isLoading={isFetching}
            data={data?.data || []}
            rowLink={(row) => {
              if (!permission.view) return undefined;
              return {
                pathname: NavigationRoutes.BonusPeriodView,
                query: {
                  id: row.original.id,
                },
              };
            }}
          />
        </TableContainer>
        <PaginationComponent
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
